<template>
  <div class="xiangshan-map" @click="onMapBackgroundClick">
    <div
      ref="svgLayer"
      class="map-svg-layer"
      v-html="svgMarkup"
      @click.stop="onSvgClick"
    />
    <div
      v-if="popupVisible"
      class="town-enterprise-popup"
      :style="{ left: popupX + 'px', top: popupY + 'px' }"
      @click.stop
    >
      <div class="town-enterprise-popup__title">{{ popupTitle }}</div>
      <div class="town-enterprise-popup__divider" />
      <ul v-if="popupCompanies.length" class="town-enterprise-popup__list">
        <li v-for="(name, idx) in popupCompanies" :key="idx" class="town-enterprise-popup__item">
          {{ name }}
        </li>
      </ul>
      <div v-else class="town-enterprise-popup__empty">暂无企业数据</div>
    </div>
  </div>
</template>

<script>
import xiangshanSvg from '@/assets/svg/xiangshan.svg'
import xiangshanGeo from '@/assets/xiangshan.json'

const BUILTIN_DONGCHEN = (() => {
  const pair = ['锦浪科技股份有限公司', '宁波耐森电气科技有限公司']
  return Array.from({ length: 10 }, (_, i) => pair[i % 2])
})()

const BUILTIN_BY_TOWN = {
  东陈乡: BUILTIN_DONGCHEN
}

export default {
  name: 'XiangshanMap',
  props: {
    /** { '东陈乡': ['公司A', ...], '泗洲头镇': [...] }，可与乡镇面板简称混用 */
    townEnterprises: {
      type: Object,
      default: null
    },
    /** 与左侧乡镇列表一致时用于点击后高亮匹配（如 泗洲头 对应 泗洲头镇） */
    panelTownList: {
      type: Array,
      default: null
    },
    activeTown: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      xiangshanSvg,
      svgMarkup: '',
      switchPopupTimer: null,
      townPathIdToTown: {},
      townPathMeta: [],
      popupVisible: false,
      popupX: 0,
      popupY: 0,
      popupTownName: '',
      popupCompanies: []
    }
  },
  computed: {
    popupTitle() {
      const n = this.popupCompanies.length
      return `${this.popupTownName}（${n}家）`
    }
  },
  watch: {
    activeTown: {
      handler(val) {
        this.triggerTownEffectByPanel(val)
      },
      immediate: true
    },
    svgMarkup() {
      this.$nextTick(() => {
        this.triggerTownEffectByPanel(this.activeTown)
      })
    },
    /** 产业类型等导致乡镇企业数据变化时，已打开的弹框需同步列表 */
    townEnterprises() {
      this.refreshPopupCompaniesIfOpen()
    }
  },
  mounted() {
    this.loadSvgMarkup()
  },
  methods: {
    normalizeTownName(name) {
      return (name || '').replace(/(镇|乡|街道)$/, '')
    },
    findPathByPanelTown(panelTown) {
      const layer = this.$refs.svgLayer
      if (!layer || !panelTown) {
        return null
      }
      const target = this.normalizeTownName(panelTown)
      const paths = Array.from(layer.querySelectorAll('path[id^="town-path-"], path[data-town]'))
      return (
        paths.find((p) => {
          const id = p.getAttribute('id') || ''
          const townName = p.getAttribute('data-town') || this.townPathIdToTown[id] || ''
          return this.normalizeTownName(townName) === target
        }) || null
      )
    },
    triggerTownEffectByPanel(panelTown) {
      if (!panelTown || !String(panelTown).trim()) {
        this.clearActivePath()
        this.popupVisible = false
        this.popupTownName = ''
        this.popupCompanies = []
        if (this.switchPopupTimer) {
          clearTimeout(this.switchPopupTimer)
          this.switchPopupTimer = null
        }
        return
      }
      const pathEl = this.findPathByPanelTown(panelTown)
      if (!pathEl || !this.$el) {
        return
      }
      const pathId = pathEl.getAttribute('id') || ''
      const geoName = pathEl.getAttribute('data-town') || this.townPathIdToTown[pathId] || ''
      if (!geoName) {
        return
      }
      const wrapRect = this.$el.getBoundingClientRect()
      const pathRect = pathEl.getBoundingClientRect()
      const x = pathRect.left + pathRect.width / 2 - wrapRect.left
      const y = pathRect.top + pathRect.height / 2 - wrapRect.top
      this.markActivePath(pathEl)
      /** 由左侧选中驱动，不向父组件 emit，否则会触发 onTownFromMap 把同一镇乡当成「再次点击」而取消选中 */
      this.openPopupAt(geoName, x, y, { emitTownClick: false })
    },
    async loadSvgMarkup() {
      try {
        const text = await fetch(this.xiangshanSvg).then((res) => res.text())
        const parser = new DOMParser()
        const doc = parser.parseFromString(text, 'image/svg+xml')
        const svg = doc.documentElement
        svg.setAttribute('class', 'map-svg-root')
        this.bindTownNameToSvgPath(svg)
        this.svgMarkup = new XMLSerializer().serializeToString(svg)
        this.$nextTick(() => {
          this.collectTownPathMeta()
        })
      } catch (e) {
        this.svgMarkup = ''
      }
    },
    collectTownPathMeta() {
      const layer = this.$refs.svgLayer
      if (!layer) {
        this.townPathMeta = []
        return
      }
      const paths = Array.from(layer.querySelectorAll('path[id^="town-path-"]'))
      this.townPathMeta = paths.map((p) => {
        const box = p.getBBox()
        return {
          el: p,
          id: p.getAttribute('id') || '',
          x: box.x + box.width / 2,
          y: box.y + box.height / 2
        }
      })
    },
    bindTownNameToSvgPath(svg) {
      const townPaths = this.extractTownPaths(svg)
      const features = (xiangshanGeo.features || []).filter(
        (item) => item && item.properties && item.properties.name
      )
      if (!townPaths.length || !features.length) {
        return
      }
      this.townPathIdToTown = {}
      townPaths.forEach((pathEl, idx) => {
        pathEl.setAttribute('id', `town-path-${idx + 1}`)
      })
      const pathCenters = this.measureSvgPathCenters(svg, townPaths)
      const geoCenters = this.computeGeoCenters(features)
      const matched = this.matchTownsByNearest(pathCenters, geoCenters)
      matched.forEach((item) => {
        item.pathEl.setAttribute('data-town', item.townName)
        const pathId = item.pathEl.getAttribute('id')
        if (pathId) {
          this.townPathIdToTown[pathId] = item.townName
        }
      })
    },
    extractTownPaths(svg) {
      const groups = Array.from(svg.querySelectorAll('g'))
      const townPaths = []
      groups.forEach((group) => {
        const directPaths = Array.from(group.children).filter((el) => el.tagName === 'path')
        if (directPaths.length === 1) {
          townPaths.push(directPaths[0])
        }
      })
      return townPaths
    },
    measureSvgPathCenters(svg, townPaths) {
      const viewBox = (svg.getAttribute('viewBox') || '').trim().split(/\s+/).map(Number)
      const vbWidth = viewBox[2] || Number(svg.getAttribute('width')) || 1
      const vbHeight = viewBox[3] || Number(svg.getAttribute('height')) || 1
      const host = document.createElement('div')
      host.style.position = 'absolute'
      host.style.left = '-10000px'
      host.style.top = '-10000px'
      host.style.visibility = 'hidden'
      const tempSvg = svg.cloneNode(true)
      host.appendChild(tempSvg)
      document.body.appendChild(host)
      const tempPaths = this.extractTownPaths(tempSvg)
      const centers = tempPaths.map((pathEl, idx) => {
        const box = pathEl.getBBox()
        return {
          pathEl: townPaths[idx],
          xNorm: (box.x + box.width / 2) / vbWidth,
          yNorm: (box.y + box.height / 2) / vbHeight
        }
      })
      document.body.removeChild(host)
      return centers
    },
    computeGeoCenters(features) {
      const centers = features.map((feature) => {
        const points = this.flattenGeoPoints(feature.geometry)
        let sumLon = 0
        let sumLat = 0
        points.forEach((p) => {
          sumLon += p[0]
          sumLat += p[1]
        })
        const n = points.length || 1
        return {
          townName: feature.properties.name,
          lon: sumLon / n,
          lat: sumLat / n
        }
      })
      const lons = centers.map((c) => c.lon)
      const lats = centers.map((c) => c.lat)
      const minLon = Math.min(...lons)
      const maxLon = Math.max(...lons)
      const minLat = Math.min(...lats)
      const maxLat = Math.max(...lats)
      const lonSpan = maxLon - minLon || 1
      const latSpan = maxLat - minLat || 1
      centers.forEach((item) => {
        item.xNorm = (item.lon - minLon) / lonSpan
        item.yNorm = (maxLat - item.lat) / latSpan
      })
      return centers
    },
    flattenGeoPoints(geometry) {
      if (!geometry || !geometry.coordinates) {
        return []
      }
      if (geometry.type === 'Polygon') {
        return geometry.coordinates.flat(1)
      }
      if (geometry.type === 'MultiPolygon') {
        return geometry.coordinates.flat(2)
      }
      return []
    },
    matchTownsByNearest(pathCenters, geoCenters) {
      const n = Math.min(pathCenters.length, geoCenters.length)
      if (!n) {
        return []
      }
      const dist = Array.from({ length: n }, (_, i) =>
        Array.from({ length: n }, (_, j) => {
          const dx = pathCenters[i].xNorm - geoCenters[j].xNorm
          const dy = pathCenters[i].yNorm - geoCenters[j].yNorm
          return dx * dx + dy * dy
        })
      )
      const totalMask = 1 << n
      const dp = new Array(totalMask).fill(Infinity)
      const choose = new Array(totalMask).fill(-1)
      dp[0] = 0
      for (let mask = 0; mask < totalMask; mask += 1) {
        const i = this.popCount(mask)
        if (i >= n || !Number.isFinite(dp[mask])) {
          continue
        }
        for (let j = 0; j < n; j += 1) {
          if (mask & (1 << j)) {
            continue
          }
          const nextMask = mask | (1 << j)
          const nextCost = dp[mask] + dist[i][j]
          if (nextCost < dp[nextMask]) {
            dp[nextMask] = nextCost
            choose[nextMask] = j
          }
        }
      }
      const assign = new Array(n).fill(-1)
      let mask = totalMask - 1
      for (let i = n - 1; i >= 0; i -= 1) {
        const j = choose[mask]
        assign[i] = j
        mask ^= 1 << j
      }
      return assign.map((geoIdx, pathIdx) => ({
        townName: geoCenters[geoIdx].townName,
        pathEl: pathCenters[pathIdx].pathEl
      }))
    },
    popCount(mask) {
      let n = mask
      let count = 0
      while (n) {
        n &= n - 1
        count += 1
      }
      return count
    },
    onMapBackgroundClick() {
      this.popupVisible = false
    },
    onSvgClick(evt) {
      const rawPathEl = evt.target && evt.target.closest ? evt.target.closest('path') : null
      const pathEl = this.resolveTownPathFromClick(evt, rawPathEl)
      if (!pathEl) {
        this.popupVisible = false
        this.clearActivePath()
        return
      }
      const wrap = this.$el
      if (!wrap) {
        return
      }
      const rect = wrap.getBoundingClientRect()
      const x = evt.clientX - rect.left
      const y = evt.clientY - rect.top
      const geoName = pathEl.getAttribute('data-town') || ''
      const pathId = pathEl.getAttribute('id') || ''
      const fallbackTown = this.townPathIdToTown[pathId] || ''
      const finalTown = geoName || fallbackTown
      if (!finalTown) {
        return
      }
      const openNextTown = () => {
        this.markActivePath(pathEl)
        this.openPopupAt(finalTown, x, y)
      }
      if (this.popupVisible && this.popupTownName && this.popupTownName !== finalTown) {
        this.popupVisible = false
        this.popupTownName = ''
        this.popupCompanies = []
        if (this.switchPopupTimer) {
          clearTimeout(this.switchPopupTimer)
        }
        this.switchPopupTimer = setTimeout(() => {
          this.switchPopupTimer = null
          openNextTown()
        }, 16)
        return
      }
      openNextTown()
    },
    resolveTownPathFromClick(evt, rawPathEl) {
      if (rawPathEl && rawPathEl.getAttribute('data-town')) {
        return rawPathEl
      }
      if (rawPathEl && rawPathEl.getAttribute('id')) {
        const id = rawPathEl.getAttribute('id')
        if (id && id.startsWith('town-path-')) {
          return rawPathEl
        }
      }
      const layer = this.$refs.svgLayer
      if (!layer || typeof evt.clientX !== 'number' || typeof evt.clientY !== 'number') {
        return null
      }
      const hitList = document.elementsFromPoint(evt.clientX, evt.clientY)
      const byStack = hitList.find((el) => {
        return (
          el &&
          el.tagName &&
          el.tagName.toLowerCase() === 'path' &&
          typeof el.getAttribute === 'function' &&
          ((el.getAttribute('data-town') && el.getAttribute('data-town').length > 0) ||
            (el.getAttribute('id') || '').startsWith('town-path-'))
        )
      })
      if (byStack) {
        return byStack
      }
      const svg = layer.querySelector('svg')
      if (!svg || typeof svg.createSVGPoint !== 'function') {
        return null
      }
      const matrix = svg.getScreenCTM()
      if (!matrix || typeof matrix.inverse !== 'function') {
        return null
      }
      const pt = svg.createSVGPoint()
      pt.x = evt.clientX
      pt.y = evt.clientY
      const local = pt.matrixTransform(matrix.inverse())
      const townPaths = Array.from(layer.querySelectorAll('path[data-town]'))
      for (let i = townPaths.length - 1; i >= 0; i -= 1) {
        const p = townPaths[i]
        if (typeof p.isPointInFill === 'function' && p.isPointInFill(local)) {
          return p
        }
      }
      if (this.townPathMeta.length) {
        let picked = null
        let best = Infinity
        this.townPathMeta.forEach((item) => {
          const dx = item.x - local.x
          const dy = item.y - local.y
          const d = dx * dx + dy * dy
          if (d < best) {
            best = d
            picked = item.el
          }
        })
        if (picked) {
          return picked
        }
      }
      return null
    },
    markActivePath(pathEl) {
      const layer = this.$refs.svgLayer
      if (!layer) {
        return
      }
      this.clearActivePath()
      pathEl.classList.add('is-active')
    },
    clearActivePath() {
      const layer = this.$refs.svgLayer
      if (!layer) {
        return
      }
      const list = layer.querySelectorAll('path.is-active')
      list.forEach((el) => el.classList.remove('is-active'))
    },
    resolveEnterpriseKey(geoName) {
      const map = this.townEnterprises
      if (map && Array.isArray(map[geoName])) {
        return geoName
      }
      const alias = {
        泗洲头镇: '泗洲头'
      }
      const viaAlias = alias[geoName]
      if (viaAlias && map && Array.isArray(map[viaAlias])) {
        return viaAlias
      }
      if (map) {
        const noSuffix = geoName.replace(/(镇|乡|街道)$/, '')
        if (Array.isArray(map[noSuffix])) {
          return noSuffix
        }
        const keys = Object.keys(map)
        const hit = keys.find(
          (k) => geoName === k || geoName.startsWith(k) || k.startsWith(geoName)
        )
        if (hit) {
          return hit
        }
      }
      return geoName
    },
    getCompaniesForTown(geoName) {
      const key = this.resolveEnterpriseKey(geoName)
      const ext = this.townEnterprises
      if (ext && Array.isArray(ext[key])) {
        return ext[key]
      }
      if (BUILTIN_BY_TOWN[geoName]) {
        return BUILTIN_BY_TOWN[geoName]
      }
      if (BUILTIN_BY_TOWN[key]) {
        return BUILTIN_BY_TOWN[key]
      }
      return []
    },
    panelTownFromGeo(geoName) {
      const alias = {
        泗洲头镇: '泗洲头'
      }
      if (alias[geoName]) {
        return alias[geoName]
      }
      const list = this.panelTownList
      if (list && list.includes(geoName)) {
        return geoName
      }
      const noSuf = geoName.replace(/(镇|乡|街道)$/, '')
      if (list && list.includes(noSuf)) {
        return noSuf
      }
      return geoName
    },
    clampPopupPosition() {
      this.$nextTick(() => {
        const wrap = this.$el
        const el = wrap && wrap.querySelector('.town-enterprise-popup')
        if (!wrap || !el) {
          return
        }
        const pad = 8
        const maxX = Math.max(pad, wrap.clientWidth - el.offsetWidth - pad)
        const maxY = Math.max(pad, wrap.clientHeight - el.offsetHeight - pad)
        this.popupX = Math.max(pad, Math.min(this.popupX, maxX))
        this.popupY = Math.max(pad, Math.min(this.popupY, maxY))
      })
    },
    refreshPopupCompaniesIfOpen() {
      if (!this.popupVisible || !this.popupTownName) {
        return
      }
      this.popupCompanies = [...this.getCompaniesForTown(this.popupTownName)]
      this.clampPopupPosition()
    },
    openPopupAt(geoName, offsetX, offsetY, options) {
      const emitTownClick = !options || options.emitTownClick !== false
      const companies = this.getCompaniesForTown(geoName)
      this.popupTownName = geoName
      this.popupCompanies = companies
      this.popupX = offsetX + 12
      this.popupY = offsetY + 12
      this.popupVisible = true
      if (emitTownClick) {
        this.$emit('town-click', {
          geoName,
          panelTown: this.panelTownFromGeo(geoName)
        })
      }
      this.clampPopupPosition()
    }
  },
  beforeUnmount() {
    if (this.switchPopupTimer) {
      clearTimeout(this.switchPopupTimer)
    }
  }
}
</script>

<style scoped>
.xiangshan-map {
  width: 100%;
  height: 100%;
  min-height: 600px;
  position: relative;
  overflow: hidden;
}

.map-svg-layer {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  pointer-events: auto;
  user-select: none;
}

.map-svg-layer ::v-deep .map-svg-root {
  width: 100%;
  height: 100%;
}

.map-svg-layer ::v-deep path {
  cursor: pointer;
  transition: fill 0.15s ease;
}

.map-svg-layer ::v-deep path:hover {
  fill: rgba(79, 166, 255, 0.86) !important;
}

.map-svg-layer ::v-deep path.is-active {
  fill: rgba(63, 150, 255, 0.96) !important;
}

.town-enterprise-popup {
  position: absolute;
  z-index: 20;
  min-width: 280px;
  max-width: 400px;
  max-height: 420px;
  padding: 22px 26px 20px;
  box-sizing: border-box;
  border-radius: 10px;
  background: rgba(238, 247, 255, 0.96);
  border: 1px solid rgba(30, 128, 255, 0.35);
  box-shadow:
    0 8px 28px rgba(30, 128, 255, 0.18),
    0 2px 10px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  pointer-events: auto;
}

.town-enterprise-popup__title {
  font-size: 18px;
  font-weight: 700;
  color: #1e80ff;
  line-height: 1.35;
  font-family:
    'PingFang SC',
    'Microsoft YaHei',
    system-ui,
    sans-serif;
}

.town-enterprise-popup__divider {
  height: 2px;
  margin: 14px 0 12px;
  background: linear-gradient(90deg, #1e80ff 0%, rgba(30, 128, 255, 0.25) 100%);
  border-radius: 1px;
}

.town-enterprise-popup__list {
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.town-enterprise-popup__item {
  font-size: 14px;
  color: #1e80ff;
  line-height: 1.75;
  text-align: left;
  font-family:
    'PingFang SC',
    'Microsoft YaHei',
    system-ui,
    sans-serif;
}

.town-enterprise-popup__empty {
  font-size: 14px;
  color: rgba(30, 128, 255, 0.55);
  text-align: center;
  padding: 12px 0;
  font-family:
    'PingFang SC',
    'Microsoft YaHei',
    system-ui,
    sans-serif;
}
</style>
